import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaCalendarAlt, FaArrowRight, FaChevronDown, FaCheck } from 'react-icons/fa';
import './FloatingEventsPanel.css';

const FloatingEventsPanel = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formRef = useRef(null);

  const events = [
    {
      title: "Salesforce AI - App Development Boot Camp",
      date: "June 25-29, 2025",
      time: "10:00 AM - 2:00 PM IST",
      description: "Hands-on training building AI-powered Salesforce apps with Einstein",
      buttonText: "Reserve Your Spot - For Free",
      color: "#00A1E0",
      id: "salesforce-bootcamp"
    },
    {
      title: "Salesforce Interview Prep Tips & Tricks",
      date: "July 2, 2025",
      time: "3:00 PM - 5:00 PM IST",
      description: "Master technical and behavioral interviews for Salesforce roles",
      buttonText: "Join Free Webinar",
      color: "#032D93",
      id: "interview-prep"
    },
    {
      title: "MERN Stack Crash Course",
      date: "July 10-14, 2025",
      time: "9:00 AM - 1:00 PM IST",
      description: "Build AI Powered full-stack applications with MongoDB, Express, React, and Node",
      buttonText: "Enroll Now - Limited Seats",
      color: "#5ED3F3",
      id: "mern-crash-course"
    }
  ];

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    event: events[0].id
  });

  // Auto-rotate events every 15-20 seconds
  useEffect(() => {
    const rotateInterval = setInterval(() => {
      if (isVisible && !showForm) {
        setCurrentEventIndex((prevIndex) => 
          prevIndex === events.length - 1 ? 0 : prevIndex + 1
        );
      }
    }, 15000 + Math.random() * 5000);

    return () => clearInterval(rotateInterval);
  }, [events.length, isVisible, showForm]);

  // Reopen panel after 15 seconds of closing
  useEffect(() => {
    let reopenTimer;
    if (!isVisible) {
      reopenTimer = setTimeout(() => {
        setIsVisible(true);
      }, 15000);
    }
    return () => clearTimeout(reopenTimer);
  }, [isVisible]);

  const handleRegisterClick = () => {
    setFormData(prev => ({
      ...prev,
      event: events[currentEventIndex].id
    }));
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formRef.current) return;

    // Split full name into first and last
    const [firstName, ...lastNameParts] = formData.fullName.trim().split(' ');
    const lastName = lastNameParts.join(' ');

    // Create hidden form for Salesforce submission
    const hiddenForm = document.createElement('form');
    hiddenForm.action = 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00DgK000002SOUb';
    hiddenForm.method = 'POST';
    hiddenForm.style.display = 'none';

    // Add required Salesforce fields
    const addHiddenField = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      hiddenForm.appendChild(input);
    };

    // Standard fields
    addHiddenField('oid', '00DgK000002SOUb');
    addHiddenField('retURL', 'http://www.codexlnc.com');
    addHiddenField('salutation', 'Mr.'); // Fixed salutation
    addHiddenField('first_name', firstName);
    addHiddenField('last_name', lastName);
    addHiddenField('email', formData.email);
    addHiddenField('phone', formData.phone);
    addHiddenField('company', 'Code XLNC'); // Fixed company
    
    // Custom fields for event registration
    addHiddenField('00NgK00000y9gYY', `Event Registration: ${formData.event}`);
    
    // Submit the form
    document.body.appendChild(hiddenForm);
    hiddenForm.submit();
    document.body.removeChild(hiddenForm);

    setIsSubmitted(true);
    setTimeout(() => {
      setShowForm(false);
      setIsSubmitted(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        event: events[0].id
      });
    }, 3000);
  };

  if (!isVisible) return null;

  return (
    <div className="floating-events-overlay">
      <div className="floating-events-center">
        {showForm ? (
          <div 
            className="events-panel registration-form"
            style={{ backgroundColor: events[currentEventIndex].color }}
          >
            {isSubmitted ? (
              <div className="success-message">
                <FaCheck className="success-icon" />
                <h3>Registration Successful!</h3>
                <p>We'll contact you with event details.</p>
              </div>
            ) : (
              <>
                <button 
                  className="close-btn"
                  onClick={() => setShowForm(false)}
                >
                  <FaTimes />
                </button>
                
                <div className="event-content">
                  <h3>Register for {events.find(e => e.id === formData.event)?.title}</h3>
                  <form ref={formRef} onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name*</label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="First and Last Name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">Email*</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="phone">Phone*</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="Phone number"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="event">Event*</label>
                      <select
                        id="event"
                        name="event"
                        value={formData.event}
                        onChange={handleChange}
                        required
                      >
                        {events.map(event => (
                          <option key={event.id} value={event.id}>
                            {event.title} - {event.date}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <button type="submit" className="register-btn">
                      Complete Registration
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div 
              className="events-panel"
              style={{ 
                transform: `rotateY(${currentEventIndex * 10}deg) rotateX(${currentEventIndex * 5}deg)`,
                backgroundColor: events[currentEventIndex].color
              }}
            >
              <button 
                className="close-btn"
                onClick={() => setIsVisible(false)}
              >
                <FaTimes />
              </button>
              
              <div className="event-content">
                <div className="event-badge">Upcoming Event</div>
                <h3>{events[currentEventIndex].title}</h3>
                <div className="event-details">
                  <p><FaCalendarAlt /> {events[currentEventIndex].date}</p>
                  <p>{events[currentEventIndex].time}</p>
                </div>
                <p className="event-description">{events[currentEventIndex].description}</p>
                <button className="register-btn" onClick={handleRegisterClick}>
                  {events[currentEventIndex].buttonText} <FaArrowRight />
                </button>
              </div>
              
              <div className="navigation-dots">
                {events.map((_, index) => (
                  <span 
                    key={index}
                    className={`dot ${index === currentEventIndex ? 'active' : ''}`}
                    onClick={() => setCurrentEventIndex(index)}
                  />
                ))}
              </div>
            </div>
            
            <button className="nav-btn prev" onClick={() => setCurrentEventIndex(prev => prev === 0 ? events.length - 1 : prev - 1)}>
              ‹
            </button>
            <button className="nav-btn next" onClick={() => setCurrentEventIndex(prev => prev === events.length - 1 ? 0 : prev + 1)}>
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FloatingEventsPanel;